import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { Card, CardContent, Dialog } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import FilterButton from 'components/atoms/FilterButton'
import { MainTemplate } from 'components'
import palette from 'theme/palette'
import { Button } from 'sputnik-ui'
import {
  fetchReleaseNotes,
  openReleaseNote,
  setPriorityFilter,
  openAllReleaseNote,
} from '../model/releaseNotesSlice'
import { trackReleaseEvent } from '../metrics'
import { formatReleaseDate } from '../utils'
import ReleaseNotesWidget from './ReleaseNotesWidget'

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
`

const Title = styled.h1`
  margin: 0;
  font-size: 28px;
  color: #202020;
`

const UnreadBadge = styled.span`
  background: ${palette.danger.color};
  color: #fff;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 600;
`

const Filters = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`

const Empty = styled.div`
  text-align: center;
  padding: 48px 24px;
  border: 1px dashed #e1e3e6;
  border-radius: 12px;
  color: #6d6d6d;
  font-size: 15px;
`

const StyledCard = styled(Card)`
  margin-bottom: 12px;
`

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`

const CardTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  color: #202020;
`

const PriorityBadge = styled.span`
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 8px;
  text-transform: capitalize;
  white-space: nowrap;
  color: ${(p) =>
    p.$priority === 'critical' || p.$priority === 'high'
      ? palette.danger.color
      : palette.neutral.color};
  background: ${(p) =>
    p.$priority === 'critical' || p.$priority === 'high'
      ? palette.danger.background
      : palette.neutral.background};
`

const CardMeta = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 8px;
  font-size: 13px;
  color: #6d6d6d;
`

const ScopeLabel = styled.span`
  background: ${(p) =>
    p.$unread ? palette.danger.background : palette.info.background};
  color: ${(p) => (p.$unread ? palette.danger.color : palette.info.color)};
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
`

const Summary = styled.p`
  margin: 10px 0 0;
  color: #3d3d4e;
  font-size: 14px;
  line-height: 1.5;
`

const Body = styled.div`
  margin: 10px 0 0;
  color: #6d6d6d;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-line;
`

const Screenshots = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
`

const ScreenshotImg = styled.img`
  max-width: 100%;
  max-height: 200px;
  border-radius: 8px;
  border: 1px solid #ededed;
  cursor: pointer;
`

const LightboxPaperProps = {
  style: {
    width: '90vw',
    height: '90vh',
    maxWidth: '90vw',
    maxHeight: '90vh',
    margin: 0,
    background: 'transparent',
    boxShadow: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
  },
}

const LightboxImg = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
`

const LightboxCloseButton = styled.button`
  position: fixed;
  top: 24px;
  right: 24px;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;

  svg {
    width: 22px;
    height: 22px;
  }
`

const LightboxNavButton = styled.button`
  position: fixed;
  top: 50%;
  ${(p) => (p.$side === 'left' ? 'left: 24px;' : 'right: 24px;')}
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;

  svg {
    width: 28px;
    height: 28px;
  }
`

const ToggleLink = styled.button`
  border: none;
  background: none;
  color: ${palette.accent.color};
  cursor: pointer;
  font-size: 13px;
  padding: 0;
  margin-top: 8px;
`

const priorities = ['all', 'critical', 'high', 'medium', 'low']

const ReleaseCenter = () => {
  const { t } = useTranslation(['release_notes', 'time'])
  const dispatch = useDispatch()
  const loading = useSelector(({ releaseNotes }) => releaseNotes.loading)
  const error = useSelector(({ releaseNotes }) => releaseNotes.error)
  const items = useSelector(({ releaseNotes }) => releaseNotes.items)
  const allRead = false
  const priority = useSelector(
    ({ releaseNotes }) => releaseNotes.priorityFilter
  )
  const [expandedIds, setExpandedIds] = useState({})
  const [lightbox, setLightbox] = useState(null)

  const closeLightbox = useCallback(() => setLightbox(null), [])

  const showPrevScreenshot = useCallback(() => {
    setLightbox((prev) =>
      prev
        ? {
            ...prev,
            index: (prev.index - 1 + prev.urls.length) % prev.urls.length,
          }
        : prev
    )
  }, [])

  const showNextScreenshot = useCallback(() => {
    setLightbox((prev) =>
      prev ? { ...prev, index: (prev.index + 1) % prev.urls.length } : prev
    )
  }, [])

  useEffect(() => {
    if (!lightbox) return undefined
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeLightbox()
      if (e.key === 'ArrowLeft') showPrevScreenshot()
      if (e.key === 'ArrowRight') showNextScreenshot()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightbox, closeLightbox, showPrevScreenshot, showNextScreenshot])

  useEffect(() => {
    dispatch(fetchReleaseNotes())
  }, [dispatch])

  // /whats-new?release=<id> — открыть конкретную ноту (ссылка «Что изменилось»
  // с главной, business_apps#1486 §4.1): раскрываем, отмечаем прочитанной,
  // снимаем фильтр приоритета, чтобы она не оказалась скрытой.
  // Применяется один раз на значение параметра: дальше фильтры, раскрытие и
  // «Прочитать все» работают как обычно.
  const location = useLocation()
  const releaseParam = new URLSearchParams(location.search).get('release')
  const appliedRelease = useRef(null)
  // Прокрутка — отдельно от применения: карточки появляются только после
  // загрузки, а виджет релизов может перезагрузить список ещё раз.
  const scrollTarget = useRef(null)
  useEffect(() => {
    if (!releaseParam || appliedRelease.current === releaseParam) return
    const item = items.find((i) => String(i.id) === releaseParam)
    if (!item) return
    appliedRelease.current = releaseParam
    dispatch(setPriorityFilter('all'))
    setExpandedIds((prev) => ({ ...prev, [item.id]: true }))
    if (!item.opened) {
      dispatch(openReleaseNote(item.id))
      trackReleaseEvent('release_open', {
        source: 'whats_new_link',
        releaseId: item.id,
        priority: item.priority,
      })
    }
    scrollTarget.current = item.id
  }, [releaseParam, items, dispatch])

  const filteredItems = useMemo(
    () =>
      items.filter((item) => {
        if (priority !== 'all' && item.priority !== priority) return false
        return true
      }),
    [items, priority]
  )

  const unreadCount = items.filter((i) => !i.opened).length

  // Уже загруженные карточки не прячем на время повторной загрузки: иначе
  // страница схлопывается и прокрутка к ноте теряется.
  const showItems = !loading || items.length > 0

  useEffect(() => {
    if (!scrollTarget.current || !showItems) return
    const el = document.getElementById(`release-note-${scrollTarget.current}`)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    scrollTarget.current = null
  }, [showItems, filteredItems, expandedIds])

  const handleOpenAllReleaseNote = () => {
    dispatch(openAllReleaseNote())
    dispatch(fetchReleaseNotes())
  }
  const toggleExpand = (item) => {
    setExpandedIds((prev) => ({
      ...prev,
      [item.id]: !prev[item.id],
    }))
    if (!item.opened) {
      dispatch(openReleaseNote(item.id))
      trackReleaseEvent('release_open', {
        source: 'release_center',
        releaseId: item.id,
        priority: item.priority,
      })
    }
  }

  return (
    <MainTemplate>
      <Header>
        <Title>{t('release_notes:center.title', 'Что нового')}</Title>
        {unreadCount > 0 && (
          <UnreadBadge>
            {t('release_notes:center.unread_count', 'Непрочитано: {{count}}', {
              count: unreadCount,
            })}
          </UnreadBadge>
        )}
        <Button
          variant="v2"
          color="primary"
          rounded
          disabled={loading || allRead}
          onClick={handleOpenAllReleaseNote}
        >
          {t(`release_notes:actions.read_all`, 'Прочитать все')}
        </Button>
      </Header>

      <Filters>
        {priorities.map((value) => (
          <FilterButton
            key={value}
            selected={priority === value}
            onClick={() => dispatch(setPriorityFilter(value))}
          >
            {t(`release_notes:priority.${value}`)}
          </FilterButton>
        ))}
      </Filters>

      {loading && !showItems && (
        <Empty>
          {t('release_notes:center.loading', 'Загрузка релизов...')}
        </Empty>
      )}
      {error && !loading && (
        <Empty>
          {t('release_notes:center.load_error', 'Не удалось загрузить релизы')}
        </Empty>
      )}

      {!loading && !error && filteredItems.length === 0 && !loading && (
        <Empty>
          {t(
            'release_notes:center.empty',
            'Все обновления прочитаны или не попадают в выбранные фильтры.'
          )}
        </Empty>
      )}

      {showItems &&
        !error &&
        filteredItems.map((item) => {
          const isExpanded = expandedIds[item.id]
          return (
            <StyledCard key={item.id} id={`release-note-${item.id}`}>
              <CardContent>
                <CardTop>
                  <CardTitle>{item.title}</CardTitle>
                  <PriorityBadge $priority={item.priority}>
                    {t(`release_notes:priority.${item.priority}`)}
                  </PriorityBadge>
                </CardTop>
                <CardMeta>
                  <ScopeLabel>
                    {t(`release_notes:scope.${item.product_scope}`) ||
                      item.product_scope}
                  </ScopeLabel>
                  <ScopeLabel $unread={!item.opened}>
                    {item.opened
                      ? t(`release_notes:read`)
                      : t(`release_notes:unread`)}
                  </ScopeLabel>
                  <span>{formatReleaseDate(item.published_at, t)}</span>
                </CardMeta>
                <Summary>{item.summary}</Summary>
                {isExpanded && (
                  <>
                    <Body>{item.body}</Body>
                    {item.attachments && item.attachments.length > 0 && (
                      <Screenshots>
                        {item.attachments.map((url, i) => (
                          <ScreenshotImg
                            key={`${item.id}-${url}`}
                            src={url}
                            alt={`Screenshot ${i + 1}`}
                            onClick={() =>
                              setLightbox({ urls: item.attachments, index: i })
                            }
                          />
                        ))}
                      </Screenshots>
                    )}
                  </>
                )}
                <ToggleLink onClick={() => toggleExpand(item)} type="button">
                  {isExpanded
                    ? t('release_notes:center.collapse', 'Свернуть')
                    : t('release_notes:center.expand', 'Подробнее')}
                </ToggleLink>
              </CardContent>
            </StyledCard>
          )
        })}
      <ReleaseNotesWidget />

      <Dialog
        open={!!lightbox}
        onClose={closeLightbox}
        maxWidth={false}
        PaperProps={{ ...LightboxPaperProps, onClick: closeLightbox }}
      >
        <LightboxCloseButton
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            closeLightbox()
          }}
        >
          <CloseIcon />
        </LightboxCloseButton>
        {lightbox && lightbox.urls.length > 1 && (
          <>
            <LightboxNavButton
              type="button"
              $side="left"
              onClick={(e) => {
                e.stopPropagation()
                showPrevScreenshot()
              }}
            >
              <ChevronLeftIcon />
            </LightboxNavButton>
            <LightboxNavButton
              type="button"
              $side="right"
              onClick={(e) => {
                e.stopPropagation()
                showNextScreenshot()
              }}
            >
              <ChevronRightIcon />
            </LightboxNavButton>
          </>
        )}
        {lightbox && (
          <LightboxImg
            src={lightbox.urls[lightbox.index]}
            alt=""
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </Dialog>
    </MainTemplate>
  )
}

export default ReleaseCenter
